import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, Chip, IconButton, InputAdornment, Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Switch, FormControlLabel,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SpeedIcon from '@mui/icons-material/Speed';
import NotesIcon from '@mui/icons-material/Notes';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import AcessoNegado from '../components/AcessoNegado';
import { formatDias } from '../utils/formatters';
import { abrirContrato, validarArquivoContrato, EXTENSOES_CONTRATO } from '../components/contratoUtils';
import { contratoInformado, formatarReal } from '../components/financeiro/financeiroUtils';
import dayjs from 'dayjs';
import ContratosComercial from '../components/contratos/ContratosComercial';

const statusConfig = {
  planejamento: { label: 'Planejamento', color: 'default'  },
  em_andamento: { label: 'Em andamento', color: 'primary'  },
  pausado:      { label: 'Pausado',      color: 'warning'  },
  concluido:    { label: 'Concluído',    color: 'success'  },
  cancelado:    { label: 'Cancelado',    color: 'error'    },
};

function SecaoForm({ titulo, icone, cor, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5, pb: 0.8, borderBottom: `2px solid ${cor}` }}>
        {icone}
        <Typography sx={{ fontWeight: 700, fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: cor }}>
          {titulo}
        </Typography>
      </Box>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );
}

const FORM_VAZIO = {
  nome: '',
  cliente: '',
  data_inicio: '',
  data_fim: '',
  valor_contrato: '',
  carga_total_contratada: '',
  quantidade_workshops: '',
  escopo: '',
  sponsor: '',
  coordenador_corporativo: '',
};

const CLIENTE_NOVO_VAZIO = {
  nome: '',
  codigo_agenda: '',
  segmento: '',
  contato_nome: '',
  contato_email: '',
  contato_telefone: '',
};

const tamanhoLegivel = (bytes) => (bytes < 1024 * 1024
  ? `${Math.max(1, Math.round(bytes / 1024))} KB`
  : `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`);

export default function Comercial() {
  const { temAcesso } = useAuth();
  const acessoLiberado = temAcesso('comercial');
  // Com o módulo "Contratos": uma lista só (contratos novos + projetos antigos), e a venda nova
  // começa sempre pelo contrato. Sem o módulo, a tela é a de sempre.
  const temContratos = true;   // v1.17.0: contratos liberado para todos

  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [abrirModal, setAbrirModal] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [clienteNovo, setClienteNovo] = useState(false);
  const [formClienteNovo, setFormClienteNovo] = useState(CLIENTE_NOVO_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [projetoEditando, setProjetoEditando] = useState(null);   // null = cadastro novo
  const [arquivo, setArquivo] = useState(null);
  const [erroArquivo, setErroArquivo] = useState('');
  const [motivoValor, setMotivoValor] = useState('');
  const [editarCliente, setEditarCliente] = useState(false);
  const [formClienteEdit, setFormClienteEdit] = useState(CLIENTE_NOVO_VAZIO);
  const modoEdicao = Boolean(projetoEditando);

  const carregar = () => {
    if (!acessoLiberado) { setLoading(false); return; }
    Promise.all([
      api.get('/clientes/'),
      api.get('/projetos/'),
    ]).then(([cli, proj]) => {
      setClientes(cli.data);
      setProjetos(proj.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, [acessoLiberado]);

  if (!acessoLiberado) {
    return (
      <AcessoNegado mensagem="Você não tem acesso ao Comercial. Fale com o Admin se precisar desse acesso." />
    );
  }

  const limparAnexo = () => { setArquivo(null); setErroArquivo(''); };

  const handleAbrirModal = () => {
    setProjetoEditando(null);
    setForm(FORM_VAZIO);
    setFormClienteNovo(CLIENTE_NOVO_VAZIO);
    setClienteNovo(false);
    setEditarCliente(false);
    setMotivoValor('');
    limparAnexo();
    setErro('');
    setAbrirModal(true);
  };

  const dadosDoCliente = (id) => {
    const c = clientes.find((x) => x.id === id);
    return {
      nome: c?.nome || '',
      codigo_agenda: c?.codigo_agenda || '',
      segmento: c?.segmento || '',
      contato_nome: c?.contato_nome || '',
      contato_email: c?.contato_email || '',
      contato_telefone: c?.contato_telefone || '',
    };
  };

  // Lápis da tabela: abre o mesmo formulário do cadastro, já preenchido.
  const handleAbrirEdicao = (p) => {
    setProjetoEditando(p);
    setForm({
      nome: p.nome || '',
      cliente: p.cliente || '',
      data_inicio: p.data_inicio || '',
      data_fim: p.data_fim || '',
      valor_contrato: p.valor_contrato ?? '',
      carga_total_contratada: p.carga_total_contratada ?? '',
      quantidade_workshops: p.workshops_vendidos ?? 0,
      escopo: p.escopo || '',
      sponsor: p.sponsor || '',
      coordenador_corporativo: p.coordenador_corporativo || '',
    });
    setFormClienteNovo(CLIENTE_NOVO_VAZIO);
    setClienteNovo(false);
    setEditarCliente(false);
    setFormClienteEdit(dadosDoCliente(p.cliente));
    setMotivoValor('');
    limparAnexo();
    setErro('');
    setAbrirModal(true);
  };

  const escolherArquivo = (e) => {
    const escolhido = e.target.files?.[0] || null;
    e.target.value = '';           // permite escolher o mesmo arquivo de novo
    if (!escolhido) return;
    const problema = validarArquivoContrato(escolhido);
    setErroArquivo(problema || '');
    setArquivo(problema ? null : escolhido);
  };

  const textoDoErro = (err, padrao) => {
    const detalhe = err?.response?.data;
    const primeiroCampo = detalhe && typeof detalhe === 'object' ? Object.values(detalhe)[0] : null;
    return (Array.isArray(primeiroCampo) ? primeiroCampo[0] : primeiroCampo) || detalhe?.detail || padrao;
  };

  const valorAlterado = modoEdicao
    && Number(form.valor_contrato) !== Number(projetoEditando.valor_contrato ?? 0);
  const jaTinhaValor = modoEdicao && contratoInformado(projetoEditando);

  const validarFormulario = () => {
    if (!form.nome.trim()) return 'O nome do projeto é obrigatório.';
    if (!clienteNovo && !form.cliente) return 'Selecione o cliente, ou cadastre um novo.';
    if (clienteNovo && !formClienteNovo.nome.trim()) return 'O nome do cliente novo é obrigatório.';
    if (editarCliente && !formClienteEdit.nome.trim()) return 'O nome do cliente não pode ficar em branco.';
    if (!(Number(form.valor_contrato) > 0)) return 'Informe o valor total do contrato.';
    if (!modoEdicao && !arquivo) return 'Anexe o contrato assinado para cadastrar o projeto.';
    const q = form.quantidade_workshops;
    if (q !== '' && (!Number.isInteger(Number(q)) || Number(q) < 0)) return 'A quantidade de workshops deve ser um número inteiro (0 ou mais).';
    return '';
  };

  const handleSalvarProjeto = async () => {
    setErro('');
    const problema = validarFormulario();
    if (problema) { setErro(problema); return; }

    setSalvando(true);
    try {
      let clienteId = form.cliente;

      if (clienteNovo) {
        const payloadCliente = { ...formClienteNovo };
        Object.keys(payloadCliente).forEach((k) => {
          if (!payloadCliente[k]) delete payloadCliente[k];
        });
        const resCliente = await api.post('/clientes/', payloadCliente);
        clienteId = resCliente.data.id;
      } else if (modoEdicao && editarCliente) {
        const original = dadosDoCliente(clienteId);
        const mudou = Object.keys(formClienteEdit).some((k) => (formClienteEdit[k] || '') !== (original[k] || ''));
        if (mudou) await api.patch(`/clientes/${clienteId}/`, formClienteEdit);
      }

      if (modoEdicao) {
        await salvarEdicao(clienteId);
      } else {
        await salvarNovo(clienteId);
      }

      setSucesso(modoEdicao ? 'Projeto atualizado com sucesso!' : 'Projeto cadastrado com sucesso!');
      setAbrirModal(false);
      carregar();
    } catch (err) {
      setErro(err?.mensagemFinal || textoDoErro(err, modoEdicao ? 'Erro ao salvar o projeto.' : 'Erro ao cadastrar o projeto.'));
    } finally {
      setSalvando(false);
    }
  };

  const salvarNovo = async (clienteId) => {
    // Cadastro novo vai como formulário com arquivo (multipart), tudo numa chamada só.
    const dados = new FormData();
    dados.append('nome', form.nome);
    dados.append('cliente', clienteId);
    dados.append('status', 'planejamento');
    dados.append('valor_contrato', String(form.valor_contrato));
    dados.append('carga_total_contratada', form.carga_total_contratada || 0);
    ['escopo', 'sponsor', 'coordenador_corporativo', 'data_inicio', 'data_fim'].forEach((campo) => {
      if (form[campo]) dados.append(campo, form[campo]);
    });
    dados.append('contrato_arquivo', arquivo);
    const resProjeto = await api.post('/projetos/', dados);
    const quantidade = parseInt(form.quantidade_workshops, 10) || 0;
    if (quantidade > 0) {
      await api.post(`/projetos/${resProjeto.data.id}/definir-workshops/`, { quantidade });
    }
  };

  const salvarEdicao = async (clienteId) => {
    const id = projetoEditando.id;
    const payload = {
      nome: form.nome,
      cliente: clienteId,
      data_inicio: form.data_inicio || null,
      data_fim: form.data_fim || null,
      carga_total_contratada: form.carga_total_contratada === '' ? 0 : form.carga_total_contratada,
      escopo: form.escopo,
      sponsor: form.sponsor,
      coordenador_corporativo: form.coordenador_corporativo,
    };
    if (valorAlterado) {
      payload.valor_contrato = String(form.valor_contrato);
      payload.motivo_valor_contrato = motivoValor.trim();
    }
    await api.patch(`/projetos/${id}/`, payload);

    const quantidade = form.quantidade_workshops === '' ? 0 : parseInt(form.quantidade_workshops, 10);
    if (quantidade !== (projetoEditando.workshops_vendidos ?? 0)) {
      try {
        await api.post(`/projetos/${id}/definir-workshops/`, { quantidade });
      } catch (err) {
        const e = new Error('workshops');
        e.mensagemFinal = `Os dados foram salvos, mas os workshops não: ${textoDoErro(err, 'erro ao ajustar a quantidade.')}`;
        throw e;
      }
    }
    if (arquivo) {
      const dados = new FormData();
      dados.append('contrato_arquivo', arquivo);
      try {
        await api.patch(`/projetos/${id}/`, dados);
      } catch (err) {
        const e = new Error('arquivo');
        e.mensagemFinal = `Os dados foram salvos, mas o novo contrato não foi enviado: ${textoDoErro(err, 'erro no envio do arquivo.')}`;
        throw e;
      }
    }
  };

  const handleAbrirContrato = async (p) => {
    try {
      await abrirContrato(p);
    } catch (err) {
      setErro(err.message);
    }
  };

  if (loading) return <Carregando />;

  return (
    <Box>
      <PageHeader
        titulo="Comercial"
        acoes={!temContratos ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAbrirModal}
            sx={{ backgroundColor: '#13375F' }}
          >
            Novo Projeto
          </Button>
        ) : null}
      />

      {temContratos && (
        <>
          {erro && !abrirModal && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>
          )}
          {sucesso && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>
          )}
          <ContratosComercial
            clientes={clientes}
            recarregarClientes={carregar}
            projetosAntigos={projetos.filter((p) => !p.contrato_novo_id)}
            onEditarAntigo={handleAbrirEdicao}
            onAbrirContratoAntigo={handleAbrirContrato}
          />
        </>
      )}

      {!temContratos && (<>

      {erro && !abrirModal && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>
      )}

      {sucesso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>
          {sucesso}
        </Alert>
      )}

      {projetos.length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" mb={1}>
              Nenhum projeto cadastrado ainda
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clique em "Novo Projeto" para registrar a primeira venda.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Projeto</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Valor do contrato</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Carga contratada</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Workshops vendidos</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Contrato</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cadastrado em</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Editar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projetos.map((p) => {
                const st = statusConfig[p.status] || statusConfig.planejamento;
                return (
                  <TableRow key={p.id}>
                    <TableCell>{p.nome}</TableCell>
                    <TableCell>{p.cliente_nome}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {contratoInformado(p) ? formatarReal(Number(p.valor_contrato)) : (
                        <Chip label="Não informado" size="small" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">{formatDias(p.carga_total_contratada)}</TableCell>
                    <TableCell align="right">{p.workshops_vendidos ?? 0}</TableCell>
                    <TableCell><Chip label={st.label} color={st.color} size="small" /></TableCell>
                    <TableCell>
                      {p.tem_contrato ? (
                        <Tooltip title={`Abrir contrato${p.contrato_nome ? `: ${p.contrato_nome}` : ''}`}>
                          <IconButton size="small" aria-label={`Abrir contrato de ${p.nome}`} onClick={() => handleAbrirContrato(p)}>
                            <DescriptionOutlinedIcon fontSize="small" sx={{ color: '#13375F' }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Chip label="Sem contrato" size="small" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{p.criado_em ? dayjs(p.criado_em).format('DD/MM/YYYY') : '—'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar projeto">
                        <IconButton size="small" aria-label={`Editar ${p.nome}`} onClick={() => handleAbrirEdicao(p)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      </>)}

      {/* Modal Novo Projeto */}
      <Dialog open={abrirModal} onClose={salvando ? undefined : () => setAbrirModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>{modoEdicao ? 'Editar Projeto' : 'Novo Projeto'}</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          {modoEdicao ? 'Altere o que for preciso e salve' : 'Preencha os dados da venda para cadastrar o projeto'}
        </Typography>
        <DialogContent>
          {erro && abrirModal && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

          <SecaoForm titulo="Cliente" icone={<BusinessIcon sx={{ fontSize: 17, color: '#2563EB' }} />} cor="#2563EB">
            {!clienteNovo ? (
              <>
                <Grid item xs={12}>
                  <Autocomplete
                    sx={{ width: '100%' }}
                    options={clientes}
                    getOptionLabel={(c) => c.nome}
                    isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id}
                    value={clientes.find((c) => c.id === form.cliente) || null}
                    onChange={(e, novoValor) => setForm({ ...form, cliente: novoValor?.id || '' })}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth required label="Cliente" placeholder="Buscar cliente..." />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button size="small" onClick={() => { setClienteNovo(true); setEditarCliente(false); }} sx={{ color: '#2563EB' }}>
                    + Cliente novo? Cadastre aqui
                  </Button>
                </Grid>
                {modoEdicao && form.cliente && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={editarCliente}
                          onChange={(e) => {
                            setEditarCliente(e.target.checked);
                            if (e.target.checked) setFormClienteEdit(dadosDoCliente(form.cliente));
                          }}
                        />
                      )}
                      label="Editar os dados deste cliente"
                    />
                    {editarCliente && (
                      <Alert severity="info" sx={{ mb: 1.5, mt: 0.5 }}>
                        A alteração vale para todos os projetos deste cliente.
                      </Alert>
                    )}
                    {editarCliente && (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField fullWidth required label="Nome do cliente" value={formClienteEdit.nome}
                            onChange={(e) => setFormClienteEdit({ ...formClienteEdit, nome: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Código na Agenda" value={formClienteEdit.codigo_agenda}
                            onChange={(e) => setFormClienteEdit({ ...formClienteEdit, codigo_agenda: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth label="Segmento" value={formClienteEdit.segmento}
                            onChange={(e) => setFormClienteEdit({ ...formClienteEdit, segmento: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Contato — nome" value={formClienteEdit.contato_nome}
                            onChange={(e) => setFormClienteEdit({ ...formClienteEdit, contato_nome: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Contato — e-mail" type="email" value={formClienteEdit.contato_email}
                            onChange={(e) => setFormClienteEdit({ ...formClienteEdit, contato_email: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth label="Contato — telefone" value={formClienteEdit.contato_telefone}
                            onChange={(e) => setFormClienteEdit({ ...formClienteEdit, contato_telefone: e.target.value })} />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth required label="Nome do cliente"
                    value={formClienteNovo.nome}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, nome: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Código na Agenda"
                    value={formClienteNovo.codigo_agenda}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, codigo_agenda: e.target.value })}
                    helperText="Ex: RCH, BET, SKA"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label="Segmento"
                    value={formClienteNovo.segmento}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, segmento: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth label="Contato — nome"
                    value={formClienteNovo.contato_nome}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, contato_nome: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth label="Contato — e-mail" type="email"
                    value={formClienteNovo.contato_email}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, contato_email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth label="Contato — telefone"
                    value={formClienteNovo.contato_telefone}
                    onChange={(e) => setFormClienteNovo({ ...formClienteNovo, contato_telefone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    CNPJ, endereço, célula e outros dados podem ser completados depois na tela de Clientes.
                  </Typography>
                  <Button size="small" onClick={() => setClienteNovo(false)} sx={{ color: '#2563EB' }}>
                    Usar um cliente já cadastrado
                  </Button>
                </Grid>
              </>
            )}
          </SecaoForm>

          <SecaoForm titulo="Informações do projeto" icone={<InfoOutlinedIcon sx={{ fontSize: 17, color: '#13375F' }} />} cor="#13375F">
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Nome do projeto"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
            </Grid>
            {!modoEdicao && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Este projeto nasce com status "Planejamento" — quem tiver permissão inicia o projeto depois, pelo botão "Iniciar projeto" na tela do projeto.
                </Typography>
              </Grid>
            )}
          </SecaoForm>

          <SecaoForm titulo="Cronograma" icone={<CalendarTodayIcon sx={{ fontSize: 15, color: '#D97706' }} />} cor="#D97706">
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#64748b', fontWeight: 600 }}>
                Data início
              </Typography>
              <TextField
                fullWidth type="date"
                value={form.data_inicio}
                onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#64748b', fontWeight: 600 }}>
                Data fim
              </Typography>
              <TextField
                fullWidth type="date"
                value={form.data_fim}
                onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
              />
            </Grid>
          </SecaoForm>

          <SecaoForm titulo="Valor do contrato" icone={<PaidOutlinedIcon sx={{ fontSize: 17, color: '#13375F' }} />} cor="#13375F">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Valor total do contrato" type="number"
                value={form.valor_contrato}
                onChange={(e) => setForm({ ...form, valor_contrato: e.target.value })}
                slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> }, htmlInput: { min: 0, step: '0.01' } }}
                helperText={jaTinhaValor ? `Valor atual: ${formatarReal(Number(projetoEditando.valor_contrato))}. Se houve aditivo, informe o novo total.` : 'Total vendido. Segue com o projeto para o Financeiro'}
              />
            </Grid>
            {valorAlterado && jaTinhaValor && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Motivo da alteração (opcional)"
                  value={motivoValor} onChange={(e) => setMotivoValor(e.target.value)}
                  placeholder="Ex.: Aditivo 01"
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  helperText="Fica no histórico do valor"
                />
              </Grid>
            )}
          </SecaoForm>

          <SecaoForm titulo="Contrato assinado" icone={<AttachFileIcon sx={{ fontSize: 17, color: '#7C3AED' }} />} cor="#7C3AED">
            <Grid item xs={12}>
              {modoEdicao && (
                <Box sx={{ mb: 1.5 }}>
                  {projetoEditando.tem_contrato ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <DescriptionOutlinedIcon fontSize="small" sx={{ color: '#13375F' }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{projetoEditando.contrato_nome || 'Contrato anexado'}</Typography>
                      <Button size="small" onClick={() => handleAbrirContrato(projetoEditando)} sx={{ textTransform: 'none' }}>
                        Abrir
                      </Button>
                    </Box>
                  ) : (
                    <Alert severity="warning" sx={{ py: 0 }}>Este projeto ainda não tem contrato anexado. Anexe abaixo.</Alert>
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  component="label" variant={arquivo ? 'outlined' : 'contained'} size="small"
                  startIcon={<AttachFileIcon />}
                  sx={arquivo ? { textTransform: 'none' } : { textTransform: 'none', backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
                >
                  {arquivo ? 'Trocar arquivo' : (modoEdicao ? 'Anexar novo contrato' : 'Anexar contrato')}
                  <input type="file" hidden accept={EXTENSOES_CONTRATO.join(',')} onChange={escolherArquivo} aria-label="Arquivo do contrato" />
                </Button>
                {arquivo && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{arquivo.name}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>({tamanhoLegivel(arquivo.size)})</Typography>
                    <IconButton size="small" aria-label="Remover o arquivo escolhido" onClick={limparAnexo}>
                      <CloseOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: erroArquivo ? 'error.main' : 'text.secondary' }}>
                {erroArquivo || (modoEdicao
                  ? 'Opcional na edição: só anexe se quiser trocar o contrato atual. PDF, Word ou imagem, até 20 MB.'
                  : 'Obrigatório. PDF, Word ou imagem, até 20 MB. Sem o contrato não dá para cadastrar o projeto.')}
              </Typography>
            </Grid>
          </SecaoForm>

          <SecaoForm titulo="Carga contratada" icone={<SpeedIcon sx={{ fontSize: 17, color: '#16A34A' }} />} cor="#16A34A">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Carga total contratada (dias)" type="number"
                value={form.carga_total_contratada}
                onChange={(e) => setForm({ ...form, carga_total_contratada: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Quantidade de workshops vendidos" type="number"
                value={form.quantidade_workshops}
                onChange={(e) => setForm({ ...form, quantidade_workshops: e.target.value })}
                helperText={modoEdicao ? 'Aumentar cria workshops novos. Só dá para reduzir os que ainda não têm lançamento na Agenda.' : 'Cada workshop vira um registro próprio, pra nomear e agendar depois'}
              />
            </Grid>
          </SecaoForm>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3, pb: 0.8, borderBottom: '2px solid #94a3b8' }}>
              <NotesIcon sx={{ fontSize: 17, color: '#94a3b8' }} />
              <Typography sx={{ fontWeight: 700, fontSize: 12.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
                Detalhes adicionais
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Opcional — pode preencher agora ou depois, no detalhe do projeto.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Escopo"
                  value={form.escopo}
                  onChange={(e) => setForm({ ...form, escopo: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Sponsor do projeto"
                  value={form.sponsor}
                  onChange={(e) => setForm({ ...form, sponsor: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Coordenador corporativo"
                  value={form.coordenador_corporativo}
                  onChange={(e) => setForm({ ...form, coordenador_corporativo: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirModal(false)} disabled={salvando}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSalvarProjeto}
            disabled={salvando || (!modoEdicao && !arquivo)}
            sx={{ backgroundColor: '#13375F' }}
          >
            {salvando ? 'Salvando...' : (modoEdicao ? 'Salvar alterações' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
